import { Autocomplete, ListItem, ListItemText } from "@mui/material";
import { SxProps } from "@mui/system";
import { Controller, RegisterOptions, useFormContext } from "react-hook-form";
import CommonTextField from "../../commonTextField/CommonTextField";
import { OrderRowTemplateOption } from "./types";
import useOrderRowTemplateAutocomplete from "./useOrderRowTemplateAutocomplete";

type Props = {
  name: string;
  fullWidth?: boolean;
  isLoading?: boolean;
  inputSx?: SxProps;
  contractId?: string;
  disabled?: boolean;
  required?: boolean;
  rules?: RegisterOptions;
  error?: boolean;
};

/**
 * Autocomplete field for selecting an order row template.
 *
 * Note: No autocomplete query implemented in backend.
 * Fetches all templates for the contract and displays them.
 */
const OrderRowTemplateAutocompleteField = ({
  name,
  fullWidth = true,
  isLoading = false,
  inputSx = {},
  contractId,
  disabled,
  required = false,
  rules,
  error = false,
}: Props) => {
  const { orderRowTemplateOptions } = useOrderRowTemplateAutocomplete({
    contractId,
  });

  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        ...(required && {
          required: { value: true, message: `${name}_required` },
        }),
        ...rules,
      }}
      render={({ field: { value, onChange } }) => (
        <Autocomplete<OrderRowTemplateOption, false, undefined, undefined>
          id="order-row-template-autocomplete"
          disabled={disabled}
          fullWidth={fullWidth}
          multiple={false}
          value={value || null}
          loading={isLoading}
          options={orderRowTemplateOptions}
          getOptionLabel={(option) => option.name}
          renderOption={(props, option) => (
            <ListItem {...props} key={option.id}>
              <ListItemText>{option.name}</ListItemText>
            </ListItem>
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, newValue) => onChange(newValue)}
          renderInput={(params) => (
            <CommonTextField {...params} sx={inputSx} error={error} />
          )}
        />
      )}
    />
  );
};

export default OrderRowTemplateAutocompleteField;
